import type { LucideIcon } from 'lucide-react';
import {
  Building2,
  ClipboardList,
  FileCheck2,
  Gauge,
  LayoutDashboard,
  Leaf,
  ScrollText,
  Shield,
  Users,
} from 'lucide-react';

import type { TranslateFn } from '@/lib/i18n';

export type VsmeSectionId =
  | 'overview'
  | 'dashboard'
  | 'gap-analysis'
  | 'general-information'
  | 'environmental-disclosure'
  | 'social-disclosure'
  | 'governance-disclosure'
  | 'policies-documents'
  | 'responsibilities'
  | 'report';

export interface VsmeMetric {
  label: string;
  value: string;
  detail: string;
}

export interface VsmeSectionDefinition {
  id: VsmeSectionId;
  to: string;
  label: string;
  title: string;
  description: string;
  status: string;
  owner: string;
  reportCycle: string;
  completion: number;
  icon: LucideIcon;
  showInNavigation?: boolean;
  metrics: VsmeMetric[];
  priorities: string[];
  deliverables: string[];
}

export const VSME_SECTIONS: VsmeSectionDefinition[] = [
  {
    id: 'overview',
    to: '/vsme/overview',
    label: 'Overview',
    title: 'Overview',
    description:
      'Structured introduction to the VSME reporting standard, applicability assessment, and workflow preparation.',
    status: 'Program initialized',
    owner: 'Sustainability Office',
    reportCycle: 'FY2026 baseline',
    completion: 62,
    icon: Gauge,
    metrics: [
      { label: 'Readiness', value: '62%', detail: '6 of 10 workstreams structured' },
      {
        label: 'Priority gaps',
        value: '14',
        detail: 'Items requiring remediation before draft freeze',
      },
      { label: 'Active owners', value: '9', detail: 'Named accountable leads across functions' },
      { label: 'Evidence packs', value: '28', detail: 'Documents and datasets already linked' },
    ],
    priorities: [
      'Confirm reporting perimeter and legal entities covered by the VSME cycle.',
      'Align material topics with the baseline narrative for environmental, social, and governance sections.',
      'Track reporting blockers early so the first management review is based on complete evidence.',
    ],
    deliverables: [
      'VSME program charter with timeline, owners, and approval gates.',
      'Initial disclosure universe with mandatory and voluntary datapoints.',
      'Readiness snapshot for management and workstream leads.',
    ],
  },
  {
    id: 'dashboard',
    to: '/vsme/dashboard',
    label: 'Dashboard',
    title: 'VSME Dashboard',
    description:
      'Executive control tower for completion, data quality, overdue actions, and report readiness.',
    status: 'Live monitoring',
    owner: 'PMO and ESG team',
    reportCycle: 'Monthly refresh',
    completion: 71,
    icon: LayoutDashboard,
    metrics: [
      { label: 'Completed tasks', value: '43', detail: 'Actions closed during the current cycle' },
      { label: 'Overdue actions', value: '7', detail: 'Dependencies blocking draft completion' },
      { label: 'Data quality', value: '89%', detail: 'Validated fields without escalation' },
      { label: 'Mgmt checkpoints', value: '3/5', detail: 'Planned steering reviews completed' },
    ],
    priorities: [
      'Surface overdue dependencies before disclosure drafting is locked.',
      'Keep source-system completeness visible for each VSME workstream.',
      'Support leadership reviews with a single page that compresses progress and risks.',
    ],
    deliverables: [
      'Progress widgets for every VSME tab.',
      'Exception queue for missing owners, policies, and evidence.',
      'Management scorecard for reporting readiness.',
    ],
  },
  {
    id: 'policies-documents',
    to: '/vsme/policies-documents',
    label: 'Policy and Documents',
    title: 'Policy and Documents',
    description:
      'Upload policies, strategies, and supporting documents that help complete VSME disclosures and field suggestions.',
    status: 'Document intake and AI review',
    owner: 'Policy Office',
    reportCycle: 'Continuous maintenance',
    completion: 67,
    icon: ScrollText,
    metrics: [
      { label: 'Linked documents', value: '58', detail: 'Files connected to VSME disclosures' },
      { label: 'Needs refresh', value: '9', detail: 'Documents approaching review or expiry' },
      { label: 'Missing approvals', value: '4', detail: 'Policies drafted but not yet approved' },
      {
        label: 'Evidence folders',
        value: '13',
        detail: 'Structured folders for disclosure support',
      },
    ],
    priorities: [
      'Collect both direct policies and supporting documents that help complete VSME fields.',
      'Make document uploads traceable to disclosure mappings before drafting starts.',
      'Keep the AI suggestion workflow under user control with explicit accept and reject decisions.',
    ],
    deliverables: [
      'Four document registers for General, Environmental, Social, and Governance areas.',
      'AI extracted field suggestions linked to VSME disclosure mappings.',
      'Review queue for uploaded files awaiting accept or reject decisions.',
    ],
  },
  {
    id: 'gap-analysis',
    to: '/vsme/gap-analysis',
    label: 'Gap Analysis',
    title: 'VSME Gap Analysis',
    showInNavigation: false,
    description:
      'Identify missing controls, evidence, policies, and datapoints against the VSME disclosure baseline.',
    status: 'Assessment in progress',
    owner: 'Compliance and Internal Control',
    reportCycle: 'Rolling assessment',
    completion: 54,
    icon: ClipboardList,
    metrics: [
      { label: 'Open gaps', value: '22', detail: 'Control or evidence issues not yet resolved' },
      { label: 'Critical gaps', value: '5', detail: 'Could block external publication' },
      { label: 'Mapped controls', value: '31', detail: 'Existing controls linked to VSME topics' },
      { label: 'Remediation plans', value: '12', detail: 'Named actions already assigned' },
    ],
    priorities: [
      'Separate structural gaps from documentation-only gaps to speed remediation.',
      'Map every open item to an owner, deadline, and expected closure evidence.',
      'Use gap severity to drive what must be fixed before the first draft.',
    ],
    deliverables: [
      'Gap register with severity, owner, and due date.',
      'Evidence requests for unresolved datapoints.',
      'Remediation dashboard aligned with report milestones.',
    ],
  },
  {
    id: 'general-information',
    to: '/vsme/general-information',
    label: 'General Information',
    title: 'General Information',
    description:
      'Capture the organizational profile, reporting boundary, business model, and baseline sustainability context.',
    status: 'Foundation data capture',
    owner: 'Corporate Secretariat',
    reportCycle: 'Annual update',
    completion: 76,
    icon: Building2,
    metrics: [
      { label: 'Legal entities', value: '12', detail: 'Entities reviewed for reporting scope' },
      {
        label: 'Boundary confirmed',
        value: '91%',
        detail: 'Sites and operations already classified',
      },
      {
        label: 'Narrative blocks',
        value: '8',
        detail: 'Reusable company profile sections prepared',
      },
      { label: 'Pending reviews', value: '2', detail: 'Awaiting sign-off from finance and legal' },
    ],
    priorities: [
      'Lock the reporting boundary before disclosure drafting starts.',
      'Standardize narrative text for company profile and value chain context.',
      'Ensure every scope assumption is auditable and clearly documented.',
    ],
    deliverables: [
      'Reporting boundary statement and entity list.',
      'Company profile narrative for the report front section.',
      'Reference table for business activities and material markets.',
    ],
  },
  {
    id: 'environmental-disclosure',
    to: '/vsme/environmental-disclosure',
    label: 'Environmental Disclosures',
    title: 'Environmental Disclosures',
    description:
      'Manage environmental topics, metrics, targets, and evidence covering climate, energy, waste, and resources.',
    status: 'Data consolidation',
    owner: 'Environment and Operations',
    reportCycle: 'Quarterly evidence refresh',
    completion: 58,
    icon: Leaf,
    metrics: [
      {
        label: 'Environmental KPIs',
        value: '17',
        detail: 'Indicators currently tracked in the module',
      },
      {
        label: 'Source completeness',
        value: '73%',
        detail: 'Datasets confirmed for the reporting year',
      },
      { label: 'Missing evidence', value: '6', detail: 'Policies or logs still to be attached' },
      { label: 'Targets tracked', value: '4', detail: 'Environmental goals linked to owners' },
    ],
    priorities: [
      'Consolidate energy, emissions, waste, and water datapoints in one evidence trail.',
      'Document methodology assumptions for any estimated environmental figures.',
      'Prepare concise management narrative to explain trends, targets, and corrective actions.',
    ],
    deliverables: [
      'Environmental metric pack with methodology notes.',
      'Linked evidence for operational data and improvement programs.',
      'Draft narrative for material environmental topics.',
    ],
  },
  {
    id: 'social-disclosure',
    to: '/vsme/social-disclosure',
    label: 'Social Disclosures',
    title: 'Social Disclosures',
    description:
      'Organize workforce, health and safety, training, inclusion, and value-chain social datapoints for disclosure.',
    status: 'Cross-functional collection',
    owner: 'HR and People Operations',
    reportCycle: 'Quarterly HR sync',
    completion: 64,
    icon: Users,
    metrics: [
      {
        label: 'Workforce indicators',
        value: '15',
        detail: 'Tracked social datapoints with owners',
      },
      {
        label: 'Coverage',
        value: '84%',
        detail: 'Employee populations mapped to the disclosure boundary',
      },
      {
        label: 'Open clarifications',
        value: '4',
        detail: 'Definitions under review with HR and legal',
      },
      {
        label: 'Training records',
        value: '312',
        detail: 'Records available for disclosure support',
      },
    ],
    priorities: [
      'Normalize workforce definitions so disclosures are consistent across entities.',
      'Tie social metrics to supporting HR records and policy references.',
      'Highlight improvement actions alongside incident and engagement data.',
    ],
    deliverables: [
      'Social KPI inventory with calculation notes.',
      'Reference list for HR and H&S evidence.',
      'Draft narrative for workforce practices and outcomes.',
    ],
  },
  {
    id: 'governance-disclosure',
    to: '/vsme/governance-disclosure',
    label: 'Governance Disclosures',
    title: 'Governance Disclosures',
    description:
      'Document governance structure, oversight, ethics, controls, and sustainability decision-making.',
    status: 'Board input pending',
    owner: 'Legal, Risk, and Board Office',
    reportCycle: 'Board-cycle aligned',
    completion: 49,
    icon: Shield,
    metrics: [
      {
        label: 'Governance topics',
        value: '11',
        detail: 'Topics requiring disclosure narrative or evidence',
      },
      { label: 'Board approvals', value: '2/4', detail: 'Expected governance approvals completed' },
      {
        label: 'Policy links',
        value: '19',
        detail: 'Governance disclosures already tied to documents',
      },
      { label: 'Open escalations', value: '3', detail: 'Items awaiting management decision' },
    ],
    priorities: [
      'Clarify board and management oversight over sustainability matters.',
      'Link ethics, compliance, and risk governance to approved documentation.',
      'Capture governance actions that demonstrate active oversight rather than passive policy existence.',
    ],
    deliverables: [
      'Governance map with committee and owner responsibilities.',
      'Disclosure-ready narrative for oversight, ethics, and controls.',
      'Approval log for management and board sign-off.',
    ],
  },
  {
    id: 'responsibilities',
    to: '/vsme/responsibilities',
    label: 'Responsibilities',
    title: 'VSME Responsibilities',
    showInNavigation: false,
    description:
      'Define ownership, approval paths, and delivery accountability across the full VSME reporting workflow.',
    status: 'Ownership alignment',
    owner: 'Program Management Office',
    reportCycle: 'Monthly governance review',
    completion: 60,
    icon: Users,
    metrics: [
      { label: 'Assigned roles', value: '24', detail: 'Named owners and reviewers in the matrix' },
      {
        label: 'Unassigned tasks',
        value: '6',
        detail: 'Actions still missing clear accountability',
      },
      {
        label: 'Approval gates',
        value: '5',
        detail: 'Formal sign-off checkpoints in the workflow',
      },
      { label: 'Escalations', value: '2', detail: 'Items awaiting leadership ownership decision' },
    ],
    priorities: [
      'Turn the VSME process into a named RACI rather than an informal collaboration model.',
      'Expose approval dependencies that can delay disclosure finalization.',
      'Ensure each section has a clear preparer, reviewer, and approver.',
    ],
    deliverables: [
      'Role matrix for authors, reviewers, and approvers.',
      'Escalation path for overdue ownership decisions.',
      'Approval workflow aligned with report release milestones.',
    ],
  },
  {
    id: 'report',
    to: '/vsme/report',
    label: 'Assurance & Report Builder',
    title: 'Assurance & Report Builder',
    description:
      'Assemble the final VSME report package, track sign-offs, and confirm publication readiness.',
    status: 'Draft preparation',
    owner: 'Reporting Lead',
    reportCycle: 'Board submission package',
    completion: 46,
    icon: FileCheck2,
    metrics: [
      {
        label: 'Draft sections',
        value: '7/10',
        detail: 'Sections with narrative in first-draft state',
      },
      { label: 'Sign-offs pending', value: '5', detail: 'Approvals needed before publication' },
      {
        label: 'Evidence coverage',
        value: '78%',
        detail: 'Report statements backed by linked support',
      },
      { label: 'Publication blockers', value: '3', detail: 'Issues still blocking final release' },
    ],
    priorities: [
      'Bring section narratives, metrics, and evidence into one controlled report package.',
      'Track sign-off readiness separately from drafting completeness.',
      'Identify blockers early enough to avoid compressing the final review window.',
    ],
    deliverables: [
      'Board-ready VSME draft with linked evidence.',
      'Publication checklist with sign-off status.',
      'Final issue log for unresolved report blockers.',
    ],
  },
];

export function isVsmeSection(section: string): section is VsmeSectionId {
  return VSME_SECTIONS.some((item) => item.id === section);
}

/**
 * Get translated VSME section label.
 */
export function getVsmeSectionLabel(sectionId: VsmeSectionId, t: TranslateFn): string {
  return t(`sections.${sectionId}.label`);
}

/**
 * Get translated VSME section title.
 */
export function getVsmeSectionTitle(sectionId: VsmeSectionId, t: TranslateFn): string {
  return t(`sections.${sectionId}.title`);
}

/**
 * Get translated VSME section description.
 */
export function getVsmeSectionDescription(sectionId: VsmeSectionId, t: TranslateFn): string {
  return t(`sections.${sectionId}.description`);
}

export const VSME_NAV_SECTIONS = VSME_SECTIONS.filter((item) => item.showInNavigation !== false);

export function getVsmeSection(section: VsmeSectionId): VsmeSectionDefinition {
  const item = VSME_SECTIONS.find((entry) => entry.id === section);

  if (!item) {
    throw new Error(`Unknown VSME section: ${section}`);
  }

  return item;
}

export function getNextVsmeSection(section: VsmeSectionId): VsmeSectionDefinition | null {
  const currentIndex = VSME_NAV_SECTIONS.findIndex((item) => item.id === section);

  if (currentIndex === -1 || currentIndex === VSME_NAV_SECTIONS.length - 1) {
    return null;
  }

  return VSME_NAV_SECTIONS[currentIndex + 1] ?? null;
}
